import { call, put, takeLatest, takeEvery, select, cancelled } from 'redux-saga/effects'
import get_ from 'lodash/get'
import { generalDoFailure } from '../../../util/fetchFailure'
import { privateFetch } from '../../../fetch'
import { API_URL_PATH_KEG_COLLECTION_BOOKINGS_SEARCH } from '../../../../../constants'

import {
  FETCH_KEG_COLLECTION_BOOKINGS,
  KEG_COLLECTION_BOOKINGS_CURRENT_REQUEST_ID,
  FETCH_EXPORT_KEG_COLLECTION_BOOKINGS_TO_CSV,
} from '../../../../actions/actionTypes'

import {
  fetchKegCollectionBookingsRequest,
  fetchKegCollectionBookingsSuccess,
  fetchKegCollectionBookingsFailure,
  saveKegCollectionBookings,
  fetchExportKegCollectionBookingsToCSVRequest,
  fetchExportKegCollectionBookingsToCSVSuccess,
  fetchExportKegCollectionBookingsToCSVFailure,
  saveExportedKegCollectionBookingsCSVFile,
} from './utils'

// Caching strategy - 2 minute TTL(time to live) for identical params
const TTL_MS = 2 * 60 * 1000

const isSameParams = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {})
const isFresh = (ts, ttl = TTL_MS) => typeof ts === 'number' && Date.now() - ts < ttl

let nextRequestId = 0

function* fetchBookingsWorker(action) {
  if (!action) return

  const abortController = new AbortController()

  const { bookingsAPIParams, forceNetworkRequest: forceAPIRequest = false } = action.payload || {}

  // Check cache (redux)
  const {
    bookingsList: {
      content,
      fetchedAt,
      lastParams,
      pageSize,
      pageNumber,
      totalElements,
      totalPages,
    },
  } = yield select(state => state.kegCollectionBookings)

  const cacheHit =
    content?.length && isSameParams(bookingsAPIParams, lastParams) && isFresh(fetchedAt)

  if (cacheHit && !forceAPIRequest) {
    // Serve from cache (redux) — no network request for faster response
    yield call(doSuccess, {
      bookingsList: { content, pageSize, pageNumber, totalElements, totalPages },
      bookingsAPIParams: lastParams,
      ts: Date.now(),
    })
    return
  }

  nextRequestId += 1
  const requestId = nextRequestId

  yield put({ type: KEG_COLLECTION_BOOKINGS_CURRENT_REQUEST_ID, payload: requestId })

  const latestRequestId = yield select(
    state => state.kegCollectionBookings.bookingsList.currentRequestId
  )
  // No fresh cache — hit the API
  try {
    yield put(fetchKegCollectionBookingsRequest())
    const response = yield call(privateFetch, {
      path: API_URL_PATH_KEG_COLLECTION_BOOKINGS_SEARCH,
      params: bookingsAPIParams,
      signal: abortController.signal,
    })
    const bookings = response.data

    // A newer request is in-flight, stale, ignore this one
    if (latestRequestId !== requestId) return

    yield call(doSuccess, {
      bookingsList: bookings,
      bookingsAPIParams,
      ts: Date.now(),
    })
  } catch (error) {
    if (get_(error, 'response.status') === 404) {
      // A newer request is in-flight, stale, ignore this one
      if (latestRequestId !== requestId) return
      yield call(doSuccess, {
        bookingsList: {
          content: [],
          pageNumber: 1,
          pageSize: 1,
          totalElements: 0,
          totalPages: 0,
        },
        bookingsAPIParams,
        ts: Date.now(),
      })
      return
    }
    if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
      // Do nothing, the saga was cancelled
      return
    }
    // A newer request is in-flight, stale, ignore this one
    if (latestRequestId !== requestId) return
    yield call(doFailure, {
      error,
    })
  } finally {
    if (yield cancelled()) {
      // Saga was cancelled, abort the HTTP call
      abortController.abort()
    }
  }
}

function* fetchBookingsExportWorker() {
  const abortController = new AbortController()

  // Reuse last table params as the basis for export
  const { bookingsList, bookingsExportList } = yield select(state => state.kegCollectionBookings)

  const baseParams = bookingsList.lastParams || {}
  const exportParams = {
    ...baseParams,
    pageNumber: 1,
    // Our "fetch everything" hack
    pageSize: bookingsList.totalElements || 100000000,
  }

  const cacheHit =
    bookingsExportList.content?.length > 0 &&
    isSameParams(exportParams, bookingsExportList.lastParams) &&
    isFresh(bookingsExportList.fetchedAt)

  if (cacheHit) {
    // Already have a fresh export dataset, nothing to do
    yield put(fetchExportKegCollectionBookingsToCSVSuccess(exportParams, Date.now()))
    return
  }

  try {
    yield put(fetchExportKegCollectionBookingsToCSVRequest())

    const response = yield call(privateFetch, {
      path: API_URL_PATH_KEG_COLLECTION_BOOKINGS_SEARCH,
      params: exportParams,
      signal: abortController.signal,
    })

    const bookings = response?.data?.content || []

    yield put(saveExportedKegCollectionBookingsCSVFile(bookings))
    yield put(fetchExportKegCollectionBookingsToCSVSuccess(exportParams, Date.now()))
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.name === 'AbortError') return
    yield put(fetchExportKegCollectionBookingsToCSVFailure(error))
  } finally {
    if (yield cancelled()) abortController.abort()
  }
}

export default [
  [takeLatest, FETCH_KEG_COLLECTION_BOOKINGS, fetchBookingsWorker],
  [takeEvery, FETCH_EXPORT_KEG_COLLECTION_BOOKINGS_TO_CSV, fetchBookingsExportWorker],
]

function* doSuccess({ bookingsList, bookingsAPIParams, ts }) {
  yield put(saveKegCollectionBookings(bookingsList))
  yield put(fetchKegCollectionBookingsSuccess(bookingsAPIParams, ts))
}

function* doFailure({ error }) {
  yield call(generalDoFailure, {
    error,
    action: fetchKegCollectionBookingsFailure(error),
  })
}
